import {AutoComplete, DatePicker, Field, WithForm} from '@startlibs/form'
import {Button, SplitColumnsContainer} from '@startlibs/components'
import {_s, formatDate, getColor, media} from '@startlibs/utils';
import {useToggle} from '@startlibs/core'
import React, {useEffect, useRef} from 'react'
import _ from 'lodash/fp'
import styled from 'styled-components'

import {getDate, getDateValue} from '../../utils/utils'
import {CategoriesInput} from '../../admin/experts/hooks/useEditCategories'
import {buildValidation} from '../../utils/validation'
import {FormattedMessage, useIntl} from "react-intl";
import {defaultDateFormat} from "../../utils/dateFormat";
import {jwtGetFetcher} from '../../utils/authFetch'
import {getJwt} from '../../hooks/useJwt'
import {wrapPromise} from '../../hooks/useSuspense'
import {lazyUserInfo} from '../WithProvider'
import {OPERATOR} from '../../enums/UserRole'

let _adminUsersResource = null
const getAdminUsersResource = () => {
  if (!_adminUsersResource) {
    _adminUsersResource = wrapPromise(() =>
      jwtGetFetcher(getJwt())('/api/admin/searchUsers?first=0&rows=1000&state=ACTIVE&orderBy=DATE&statuses=ACTIVE')
        .then(r => Array.isArray(r?.list) ? r.list : [])
    )
  }
  return _adminUsersResource
}

export const lazyAdminUsers = {
  read: () => getAdminUsersResource().read(),
  refresh: () => { _adminUsersResource = null }
}

const AdvancedPopupComponent = styled.div`
  position: absolute;
  background-color: #FFF;
  border: 1px solid ${getColor('gray210')};
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  padding: 1.25rem;
  z-index: 999;
  left: 0;
  width: 100%;
  min-width: 320px;
  margin-top: -1px;
  .upload-dateshortcuts {
    margin-top: -0.25rem;
    margin-bottom: 1rem;
    a {
      color: ${getColor('gray150')};
      margin-right: 1rem;
      text-decoration: underline;
    }
  }
  .popup-buttons-container {
    text-align: right;
  }
  .close-icon {
    color: ${getColor('gray150')};
    font-size: 20px;
    position: absolute;
    top: 6px;
    right: 5px;
    cursor: pointer;
    width: 2rem;
    line-height: 2rem;
    text-align: center;
    border-radius: 50%;
    :hover {
      background: ${getColor('gray240')};
    }
  }
  ${media.desktop`
    max-width: 30rem;
  `}
`

const getCoordinatorLabel = c => `${c.firstName} ${c.lastName}`
const validateCoordinator = (v) => _.isString(v) ? {errors: "Please select a valid coordinator from the list."} : undefined

const getValuesFromParams = (params, categories, allUsers) => ({
  dob: getDateValue('dob',params),
  from: getDateValue('from',params),
  to: getDateValue('to',params),
  categories:!categories ? [] : (params.categories||"").split("|").map(key =>categories.find(({id})=>id.toString()===key)).filter(Boolean) || [],
  coordinators: (params.coordinatorIds || "").split("|").filter(Boolean).map(id =>
    allUsers.find(c => c.id.toString() === id) || {id, firstName: id, lastName: ""}
  )
})

const transform = ({from,dob,to,categories,coordinators: selectedCoordinators}) => ({
  dob: _.isNumber(dob) || _.isDate(dob) ? formatDate(dob, "MM-dd-yyyy") : "",
  from: _.isNumber(from) || _.isDate(from) ? formatDate(from, "MM-dd-yyyy") : "",
  to: _.isNumber(to) || _.isDate(to) ? formatDate(to, "MM-dd-yyyy") : "",
  categories:categories.map(_.get('id')).filter(Boolean).join("|"),
  coordinatorIds: selectedCoordinators.filter(_.isObject).map(c => c.id).join("|"),
})

const preValidation = buildValidation({
  categories: (e) => e.find(_.isString) && "Invalid division"
})

export const AdvancedSearchPopup = ({params, focusedInput, categories, withoutCreateDate, isExpert, setParams, closePopup}) => {
  const popupRef = React.useRef()
  const intl = useIntl()
  const userInfo = lazyUserInfo.read()
  const allUsers = isExpert ? [] : userInfo.role === OPERATOR ? [userInfo] : lazyAdminUsers.read()
  const getCoordinatorLabelWithYou = (c) => c.id === userInfo.id ? `${getCoordinatorLabel(c)} (you)` : getCoordinatorLabel(c)

  const focusRef = useRef()

  useEffect(() => {
    const tryClose = (e) => !(popupRef.current.contains(e.target) || e.target === popupRef.current || e.defaultPrevented) && closePopup()
    document.addEventListener("click", tryClose)
    return () => document.removeEventListener("click", tryClose)
  }, [])

  useEffect(() => {
    if (focusRef.current) {
      focusRef.current.focus()
    }
  }, [])


  const action = (values) => {
    setParams(_s.assign(values))
    closePopup()
  }

  return <WithForm
    action={action}
    values={getValuesFromParams(params, categories, allUsers)}
    transform={transform}
    preValidation={preValidation}
  >{form =>
    <AdvancedPopupComponent
    ref={popupRef}
    // onKeyDown={({key}) => key === "Enter" && form.submitForm()}
    onKeyDown={({key,defaultPrevented}) => key === "Enter" && !defaultPrevented && form.submitForm()}
    onClick={(e) => e.stopPropagation()}
  >
    {categories && <CategoriesInput autoFocus={focusedInput === "categories"}/>}
    {!isExpert && <div onClick={e => e.preventDefault()} onKeyDown={e => e.key === "Enter" && e.preventDefault()}>
      <AutoComplete
        label={intl.formatMessage({defaultMessage:"Case coordinator",description:"Case coordinator label for advanced search"})}
        path="coordinators"
        minQueryLength={1}
        clickOpensWhenEmpty
        autoSelect
        withDropdown
        confirmIfValueKeys={['Tab']}
        fetchSuggestions={query => Promise.resolve(
          allUsers
            .filter(c => getCoordinatorLabel(c).toLowerCase().includes(query.toLowerCase()))
            .sort((a, b) => getCoordinatorLabel(a).localeCompare(getCoordinatorLabel(b)))
        )}
        validation={validateCoordinator}
        getLabel={getCoordinatorLabelWithYou}
        placeholder={intl.formatMessage({defaultMessage:"Click or start typing to filter by coordinator",description:"Case coordinator filter placeholder"})}
      />
    </div>}
    <DatePicker
      ref={focusedInput === "dob" ? focusRef : undefined}
      label={intl.formatMessage({defaultMessage:"Patient date of birth",description:"Patient date of birth label for advanced search"})}
      placeholder={defaultDateFormat(intl).toUpperCase()} format={defaultDateFormat(intl)}
      path="dob"
    />
    {
      !withoutCreateDate && <DateRangeField
        form={form}
        initFocusRef={focusedInput === "from" ? focusRef : undefined}
        endFocusRef={focusedInput === "to" ? focusRef : undefined}
        label={intl.formatMessage({defaultMessage:"Case created between",description:"Case created between label for advanced search"})}
      />
    }
    <div className="popup-buttons-container">
      <Button onClick={closePopup} tabIndex={2}><FormattedMessage defaultMessage="Cancel" description="Cancel button"/></Button>
      <Button highlight type="submit"><FormattedMessage defaultMessage="Search" description="Search button"/></Button>
    </div>
  </AdvancedPopupComponent>
  }</WithForm>
}

const DateRangeField = ({label, initFocusRef, endFocusRef, form}) => {
  const intl = useIntl()
  const updateFields = useToggle()

  return <Field label={label}>
    <SplitColumnsContainer key={updateFields.isOpen}>
      <DatePicker
        ref={initFocusRef} label="" placeholder={defaultDateFormat(intl).toUpperCase()} format={defaultDateFormat(intl)}
        path="from"
      />
      <DatePicker
        ref={endFocusRef} label="" placeholder={defaultDateFormat(intl).toUpperCase()} format={defaultDateFormat(intl)}
        path="to"
      />
    </SplitColumnsContainer>
    <div className="upload-dateshortcuts">
      <a onClick={() => {form.setValue('from',getDate(-30)); form.setValue("to",getDate(0)); updateFields.openWith(Date.now())}}>
        <FormattedMessage defaultMessage="Last 30 days" description="Last 30 days search filter label"/>
      </a>
      <a onClick={() => {form.setValue('from',getDate(-7)); form.setValue("to",getDate(0)); updateFields.openWith(Date.now())}}>
        <FormattedMessage defaultMessage="Last 7 days" description="Last 7 days search filter label"/>
      </a>
      <a onClick={() => {form.setValue('from',getDate(0)); form.setValue("to",getDate(0)); updateFields.openWith(Date.now())}}>
        <FormattedMessage defaultMessage="Today" description="Today search filter label"/>
      </a>
    </div>
  </Field>
}
